import React, { useState, useEffect } from 'react';
import { Navigate, Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useTranslation, Trans } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { LanguageSwitcher } from '../ui/language-switcher';
import { Fingerprint, Shield } from 'lucide-react';
import { AuthIllustration } from './AuthIllustration';

const API_URL = process.env.REACT_APP_API_URL || '';

const SMARTID_ERROR_I18N: Record<string, string> = {
  smartid_user_cancelled: "login.smartIdUserCancelled",
  smartid_access_denied: "login.smartIdAccessDenied",
  smartid_no_code: "login.smartIdNoCode",
  smartid_invalid_state: "login.smartIdInvalidState",
  smartid_no_access: "login.smartIdNoAccess",
  smartid_callback_failed: "login.smartIdCallbackFailed",
  smartid_auth_failed: "login.smartIdAuthFailed",
  // Provider-neutral: Google OAuth failures land here too (ticket 11)
  auth_failed: "login.authFailed",
  session_error: "login.sessionError",
  smartid_unsupported_country: "login.smartIdUnsupportedCountry",
  smartid_invalid_claims: "login.smartIdInvalidClaims",
  // The session guard's revocation redirect (audit F02). `not_whitelisted` is
  // deliberately absent — ticket 6 retired that error.
  session_expired: "login.errorSessionExpired",
  service_unavailable: "login.errorServiceUnavailable",
};

export function LoginPage() {
  const { t, i18n } = useTranslation();
  const { isAuthenticated, isLoading } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  // ProtectedRoute leaves the page it bounced from in state; go back there after sign-in.
  const returnTo = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [lastErrorCode, setLastErrorCode] = useState<string | null>(null);
  const [smartIdStatus, setSmartIdStatus] = useState<{ configured: boolean } | null>(null);

  // Fetch Smart-ID status (outbound IP for debug)
  useEffect(() => {
    axios.get(`${API_URL}/auth/smartid/status`, { withCredentials: true })
      .then(res => setSmartIdStatus(res.data.data))
      .catch(() => {});
  }, []);

  // Check for error in URL params (from Smart-ID callback)
  useEffect(() => {
    const errorParam = searchParams.get('error');
    if (errorParam) {
      const key = SMARTID_ERROR_I18N[errorParam];
      setError(key ? t(key) : t('login.smartIdAuthFailed'));
      setLastErrorCode(errorParam);
      // Through the router, not window.history: a raw replaceState drops the
      // state React Router keeps there, and the Back guard reads it (BUG-16).
      navigate('/login', { replace: true });
    }
  }, [searchParams, t, navigate]);

  const handleSmartIdLogin = async () => {
    setError('');
    setIsSubmitting(true);

    try {
      const locale = i18n.language || 'et';
      const response = await axios.post(
        `${API_URL}/auth/smartid/init`,
        { locale },
        { withCredentials: true }
      );

      if (response.data.success && response.data.data.authUrl) {
        window.location.href = response.data.data.authUrl;
      } else {
        setError(t('login.smartIdInitFailed'));
        setIsSubmitting(false);
      }
    } catch (err: any) {
      const message = err.response?.data?.error || t('login.smartIdAuthFailed');
      setError(message);
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-primary border-t-transparent"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to={returnTo && returnTo !== "/" ? returnTo : "/dashboard"} replace />;
  }

  return (
    <div className="min-h-screen flex">
      {/* Left side (white) */}
      <div className="flex-1 bg-card px-4 sm:px-6 py-5 flex flex-col overflow-x-hidden">
        {/* Top bar: logo + language switcher */}
        <div className="flex items-center justify-between">
          <Link to="/" className="w-fit group flex items-center gap-2">
            <img
              src="/logo.png"
              alt="Unikort"
              className="h-9 w-auto"
            />
            <span className="text-xl font-semibold text-[#5756d6] dark:text-[#777aff]">
              Unikort
            </span>
          </Link>
          <LanguageSwitcher />
        </div>

        <div className="flex-1 flex items-center justify-center">
          <div className="w-full max-w-[420px]">
            <div className="flex flex-col gap-5">
              {/* Section 1 */}
              <div className="flex flex-col gap-5">
                <div className="text-center">
                  <h1 className="text-2xl sm:text-3xl font-semibold text-foreground tracking-tight">
                    {t('login.welcomeBack')}
                  </h1>
                  <p className="text-sm sm:text-base text-muted-foreground mt-2">
                    {t('login.signInToContinue')}
                  </p>
                </div>

                {error && (
                  <div className="bg-destructive/10 text-destructive text-sm p-4 rounded-xl border border-destructive/20">
                    {error}
                  </div>
                )}

                <div className="flex flex-col items-center text-center py-4">
                  <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                    <Fingerprint className="w-8 h-8 text-primary" />
                  </div>
                  <h3 className="text-lg font-medium text-foreground mb-2">
                    {t('login.smartIdTitle')}
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4 max-w-[280px]">
                    {t('login.smartIdDescription')}
                  </p>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Shield className="w-4 h-4" />
                    <span>{t('login.secureAuth')}</span>
                  </div>
                </div>
              </div>

              {/* Section 2 */}
              <div className="flex flex-col gap-3 pt-2">
                <Button
                  type="button"
                  size="lg"
                  className="w-full"
                  disabled={isSubmitting}
                  onClick={handleSmartIdLogin}
                >
                  {isSubmitting ? t('login.redirecting') : t('login.continueWithSmartId')}
                </Button>

                {/* Neutral legal notice (ticket 5) — deliberately NOT an assent claim */}
                <p className="text-xs text-muted-foreground text-center">
                  <Trans i18nKey="login.legalNotice">
                    Use of the service is governed by the <Link to="/terms" className="underline hover:text-foreground">Terms of Use</Link>; personal data is processed as described in the <Link to="/privacy" className="underline hover:text-foreground">Privacy Policy</Link>.
                  </Trans>
                </p>

              </div>
            </div>
          </div>
        </div>

        {/* Legal footer (ticket 5) */}
        <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground pt-4">
          <Link to="/terms" className="hover:text-foreground hover:underline">{t('legal.terms')}</Link>
          <span aria-hidden="true">·</span>
          <Link to="/privacy" className="hover:text-foreground hover:underline">{t('legal.privacy')}</Link>
        </div>
      </div>

      {/* Right side: brand panel (full height, no padding, no rounding) */}
      <div className="hidden lg:flex flex-1 bg-gradient-to-br from-primary to-primary-hover items-center justify-center">
        <AuthIllustration />
      </div>
    </div>
  );
}
